import sqlite3InitModule from '@sqlite.org/sqlite-wasm'

let db: any = null
let sqlite3: any = null
let initialized: boolean = false

// Generate or retrieve a persistent session ID for this user
function getSessionId(): string {
  let sessionId = localStorage.getItem('geometric-model-session-id')
  if (!sessionId) {
    sessionId = `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    localStorage.setItem('geometric-model-session-id', sessionId)
  }
  return sessionId
}

// Initialize SQLite WASM
export async function initDatabase() {
  if (db) return db

  try {
    // Initialize the SQLite WASM module
    sqlite3 = await sqlite3InitModule({
      print: console.log,
      printErr: console.error,
    })

    console.log('SQLite WASM module initialized:', sqlite3.version)

    // Get persistent session ID
    const sessionId = getSessionId()
    const dbName = `/geometric-models-${sessionId}.db`

    // Check if OPFS is available and supported
    if ('opfs' in sqlite3) {
      try {
        // Use OPFS (Origin Private File System) for persistence in browser
        db = new sqlite3.oo1.OpfsDb(dbName)
        console.log(`OPFS database initialized: ${dbName}`)
      } catch (opfsError) {
        console.warn('OPFS failed, falling back to in-memory database:', opfsError)
        // Fallback to in-memory database
        db = new sqlite3.oo1.DB(':memory:')
        console.log('In-memory database initialized')
      }
    } else {
      // Fallback to in-memory database
      db = new sqlite3.oo1.DB(':memory:')
      console.log('In-memory database initialized')
    }

    // Initialize database schema
    await initSchema()
    
    initialized = true
    return db
  } catch (error) {
    console.error('Failed to initialize SQLite WASM:', error)
    throw error
  }
}

// Initialize database schema
async function initSchema() {
  if (!db) throw new Error('Database not initialized')

  try {
    // Enable foreign keys
    db.exec('PRAGMA foreign_keys = ON')
    
    // Set journal mode to WAL for better concurrent access (if supported)
    try {
      db.exec('PRAGMA journal_mode = WAL')
    } catch (walError) {
      console.warn('WAL mode not supported, using default journal mode')
    }

    // Create tables if they don't exist
    db.exec(`
      CREATE TABLE IF NOT EXISTS geometric_models (
        id TEXT PRIMARY KEY,
        name TEXT NOT NULL,
        description TEXT,
        created_at TEXT NOT NULL,
        updated_at TEXT NOT NULL
      )
    `)

    db.exec(`
      CREATE TABLE IF NOT EXISTS geometric_primitives (
        id TEXT PRIMARY KEY,
        model_id TEXT NOT NULL,
        name TEXT,
        type TEXT NOT NULL,
        position_x REAL NOT NULL DEFAULT 0,
        position_y REAL NOT NULL DEFAULT 0,
        position_z REAL NOT NULL DEFAULT 0,
        rotation_x REAL NOT NULL DEFAULT 0,
        rotation_y REAL NOT NULL DEFAULT 0,
        rotation_z REAL NOT NULL DEFAULT 0,
        scale_x REAL NOT NULL DEFAULT 1,
        scale_y REAL NOT NULL DEFAULT 1,
        scale_z REAL NOT NULL DEFAULT 1,
        parent_id TEXT,
        order_index INTEGER NOT NULL DEFAULT 0,
        properties TEXT,
        created_at TEXT NOT NULL,
        updated_at TEXT NOT NULL,
        FOREIGN KEY (model_id) REFERENCES geometric_models(id) ON DELETE CASCADE,
        FOREIGN KEY (parent_id) REFERENCES geometric_primitives(id) ON DELETE CASCADE
      )
    `)

    // Create indexes for better performance
    db.exec(`
      CREATE INDEX IF NOT EXISTS idx_primitives_model_id 
      ON geometric_primitives(model_id)
    `)

    db.exec(`
      CREATE INDEX IF NOT EXISTS idx_primitives_created_at 
      ON geometric_primitives(created_at)
    `)

    console.log('Database schema initialized')
  } catch (error) {
    console.error('Failed to initialize database schema:', error)
    throw error
  }
}

// Get database instance (must call initDatabase first)
export function getDatabase() {
  if (!db) {
    throw new Error('Database not initialized. Call initDatabase() first.')
  }
  return db
}

// Check if database is initialized
export function isDatabaseInitialized(): boolean {
  return initialized && db !== null
}

// Get current session ID (useful for debugging)
export function getCurrentSessionId(): string {
  return getSessionId()
}

// Reset session (for testing/debugging - creates new database)
export function resetSession(): void {
  localStorage.removeItem('geometric-model-session-id')
  console.log('Session reset - new database will be created on next init')
}

export { db } 